import React from "react";
import { motion } from "framer-motion";
import { Home, Search, ArrowRight, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getOrganizationSlugFromSubdomain } from "../../lib/utils/domain";

const OrganizationNotFound: React.FC = () => {
  const navigate = useNavigate();
  const subdomainSlug = getOrganizationSlugFromSubdomain();
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  const handleGoToHome = () => {
    // If on a subdomain, redirect to main domain
    if (subdomainSlug) {
      window.location.href = "https://aiserwave.com";
    } else {
      navigate("/");
    }
  };

  return (
    <>
      <Helmet>
        <title>Organization Not Found - Aiserwave</title>
        <meta
          name="description"
          content="The organization you're looking for doesn't exist. Explore events on Aiserwave or return to the homepage."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full text-center">
          {/* Animated 404 Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-[#17265a]/10 rounded-full blur-3xl"></div>
              <div className="relative w-32 h-32 mx-auto bg-gradient-to-br from-[#17265a] to-[#2d3f7a] rounded-full flex items-center justify-center shadow-2xl">
                <AlertCircle className="w-16 h-16 text-white" />
              </div>
            </div>
          </motion.div>

          {/* Main Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Organization Not Found
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-2">
              This link appears to be broken
            </p>
            {subdomainSlug && (
              <p className="text-sm text-gray-500 mt-2">
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {hostname}
                </span>{" "}
                does not exist
              </p>
            )}
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-10"
          >
            <p className="text-gray-600 leading-relaxed max-w-xl mx-auto">
              The organization you're looking for doesn't exist or may have been
              moved. You can explore all available events on Aiserwave or return
              to the homepage.
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGoToHome}
              className="group bg-[#17265a] text-white px-8 py-4 rounded-lg hover:bg-[#0f1a3f] transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl font-semibold text-lg w-full sm:w-auto"
            >
              <Home className="w-5 h-5" />
              <span>Go to Aiserwave.com</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (subdomainSlug) {
                  window.location.href = "https://aiserwave.com";
                } else {
                  navigate("/");
                }
              }}
              className="group bg-white border-2 border-[#17265a] text-[#17265a] px-8 py-4 rounded-lg hover:bg-[#17265a]/5 transition-all duration-300 flex items-center gap-2 font-semibold text-lg w-full sm:w-auto"
            >
              <Search className="w-5 h-5" />
              <span>Explore Events</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>

          {/* Helpful Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 pt-8 border-t border-gray-200"
          >
            <p className="text-sm text-gray-500 mb-4">Need help?</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="https://aiserwave.com/about"
                className="text-[#17265a] hover:text-[#0f1a3f] font-medium transition-colors"
              >
                About Aiserwave
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="https://aiserwave.com/pricing"
                className="text-[#17265a] hover:text-[#0f1a3f] font-medium transition-colors"
              >
                Pricing
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="https://aiserwave.com"
                className="text-[#17265a] hover:text-[#0f1a3f] font-medium transition-colors"
              >
                Home
              </a>
            </div>
          </motion.div>

          {/* Decorative Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute top-20 left-10 w-32 h-32 bg-[#17265a]/5 rounded-full blur-2xl"
            ></motion.div>
            <motion.div
              animate={{
                y: [0, 20, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
              className="absolute bottom-20 right-10 w-40 h-40 bg-[#17265a]/5 rounded-full blur-2xl"
            ></motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganizationNotFound;
